"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-16 bg-muted-light-background animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light-background animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light-background animate-pulse rounded-md" />
      </div>;
  }
  return <>
      <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList>
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="relative px-4 py-2 text-foreground font-medium transition-colors duration-[var(--duration-medium)] hover:!bg-transparent focus:!bg-transparent after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary after:scale-x-0 after:origin-left after:transition-transform after:duration-[var(--duration-medium)] after:ease-[var(--ease-smooth)] hover:after:scale-x-100">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {mobileMenuOpen && <div className="absolute top-full left-0 right-0 bg-background border-b border-border shadow-soft md:hidden z-50">
          <nav className="flex flex-col py-4 px-6 gap-2">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="py-2 text-foreground font-medium hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full bg-background/80 backdrop-blur-md border-b border-border transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isScrolled ? "h-16" : "h-[90px]"}`}>
      <div className="container mx-auto px-6 h-full flex items-center justify-between">
        <Link to="/HomePage#" className="flex-shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542870/img/equicanine_logo.png" alt="EquiCanine" className="h-7" />
        </Link>

        <MenuProvider menu_id="79" className="flex items-center gap-6">
          <HeaderMenuConsumer />
        </MenuProvider>

        <Button asChild className="hidden md:inline-flex btn-primary hover:!bg-accent-hover focus:!bg-accent-hover">
          <Link to="#contact">Get Started</Link>
        </Button>
      </div>
    </header>;
}